import { useMemo, useRef, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { FluentProvider, Link } from '@fluentui/react-components';
import { ArrowClockwiseDashesColor } from '@fluentui/react-icons';
import { formatDate, type IWorkOffer } from '@moko/utils';
import { useActiveTheme } from '../theme/ThemeContext';
import PagedDataGrid, {
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '../DataGrid/PagedDataGrid';
import type { DataGridColumn } from '../DataGrid/DataGridUI';
import type { IWorkOfferServices, WorkOfferListItem } from './types';

const PAGE_SIZE = 10;

const formatPrice = (value?: number | null): string =>
  value === undefined || value === null ? '' : String(value);

export interface WorkOffersListProps {
  workId: number;
  services: Pick<IWorkOfferServices, 'getWorkOffers'>;
  onOpenOffer: (offer: IWorkOffer) => void;
  setIsInProgress: (value: boolean) => void;
  setErrorMessage: (message: string) => void;
}

const WorkOffersList: FunctionComponent<WorkOffersListProps> = ({
  workId,
  services,
  onOpenOffer,
  setIsInProgress,
  setErrorMessage,
}) => {
  const { t } = useTranslation();
  const theme = useActiveTheme();
  const gridRef = useRef<IPagedDataGridRefType>(null);

  const columns: DataGridColumn<WorkOfferListItem>[] = useMemo(
    () => [
      {
        key: 'id',
        name: t('id', 'Id'),
        width: 60,
        isSortable: true,
        renderCell: (item: WorkOfferListItem) => (
          <Link onClick={() => onOpenOffer(item)}>{item.id}</Link>
        ),
      },
      {
        key: 'contractorName',
        name: t('contractor', 'Contractor'),
        width: 200,
        isSortable: true,
        renderCell: (item: WorkOfferListItem) =>
          item.contractorName ?? String(item.contractorId),
      },
      {
        key: 'status',
        name: t('status', 'Status'),
        width: 140,
        isSortable: true,
      },
      {
        key: 'finalOfferedPrice',
        name: t('offeredPrice', 'Offered Price'),
        width: 130,
        renderCell: (item: WorkOfferListItem) =>
          formatPrice(item.finalOfferedPrice),
      },
      {
        key: 'agreedPrice',
        name: t('agreedPrice', 'Agreed Price'),
        width: 130,
        renderCell: (item: WorkOfferListItem) => formatPrice(item.agreedPrice),
      },
      {
        key: 'responseNote',
        name: t('responseNote', 'Response Note'),
        width: 250,
        renderCell: (item: WorkOfferListItem) =>
          item.responseNote ? (
            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {item.responseNote}
            </span>
          ) : null,
      },
      {
        key: 'respondedAt',
        name: t('respondedAt', 'Responded At'),
        width: 170,
        renderCell: (item: WorkOfferListItem) =>
          formatDate(item.respondedAt, true),
      },
      {
        key: 'createdAt',
        name: t('createdAt', 'Created at'),
        width: 170,
        isSortable: true,
        renderCell: (item: WorkOfferListItem) =>
          formatDate(item.createdAt, true),
      },
    ],
    [t, onOpenOffer]
  );

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: t('refresh', 'Refresh'),
        icon: <ArrowClockwiseDashesColor />,
        onClick: (ctx) => ctx.refresh(),
      },
    ],
    [t]
  );

  return (
    <FluentProvider theme={theme} style={{ background: 'transparent' }}>
      <PagedDataGrid<WorkOfferListItem>
        ref={gridRef}
        columns={columns}
        commandBarItems={commandBarItems}
        pageSize={PAGE_SIZE}
        onRowDoubleClicked={(item) => onOpenOffer(item)}
        getAction={async (
          abortController,
          pageSize,
          pageNo,
          orderBy,
          search,
          filters
        ) => {
          const offers = await services.getWorkOffers(abortController, {
            workId,
            pageSize,
            pageNo,
            orderBy,
            search,
            filters,
          });
          return {
            ...offers,
            items: offers.items as WorkOfferListItem[],
          };
        }}
        onLoading={() => setIsInProgress(true)}
        onLoaded={() => setIsInProgress(false)}
        onError={(error) => setErrorMessage(error.message)}
      />
    </FluentProvider>
  );
};

export default WorkOffersList;
